import type { InputHTMLAttributes, ReactNode } from 'react';
import React from 'react';

import { cn } from '../../utils/cn';

interface RadioButtonProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
    label?: ReactNode;
    description?: ReactNode;
    showBorder?: boolean;
    wrapperClassName?: string;
    labelClassName?: string;
    descriptionClassName?: string;
    id: string;
    /**
     * When true, renders without the outer label wrapper.
     * Useful when composing RadioButton inside another label element.
     */
    noWrapper?: boolean;
}

const textVariants = {
    description: cn('text-sm text-midnight opacity-80 group-has-[:disabled]:text-midnight-disabled-text'),
    label: cn('text-sm text-midnight group-has-[:disabled]:text-midnight-disabled-text'),
};

/**
 * A radio button component.
 *
 * @param id - **Required.** Unique identifier for the radio button input.
 * @param label - Optional label displayed next to the radio button.
 * @param description - Optional helper text displayed below the label.
 * @param disabled - Whether the radio button is disabled.
 * @param labelClassName - Additional CSS classes to merge with default label styles.
 * @param descriptionClassName - Additional CSS classes to merge with default description styles.
 * @param className - Additional CSS classes to merge with default styles.
 * @param noWrapper - When true, renders without the outer label wrapper.
 * @param ...props - Additional HTML input attributes.
 *
 * @example
 * <RadioButton id="option1" name="choice" label="Option 1" />
 *
 * @example
 * <RadioButton
 *   id="option2"
 *   name="choice"
 *   label="Option 2"
 *   description="This is a detailed description."
 * />
 *
 * @example
 * <RadioButton
 *   id="option3"
 *   name="choice"
 *   label="Option 3"
 *   disabled
 * />
 */
export const RadioButton: React.FC<RadioButtonProps> = ({
    className,
    description,
    descriptionClassName,
    id,
    label,
    labelClassName,
    noWrapper = false,
    showBorder = false,
    wrapperClassName,
    ...props
}) => {
    const labelId = `${id}-label`;
    const descriptionId = `${id}-description`;

    const defaultClasses = cn(
        // Base styles: 20px circle with 1px border
        'peer block h-5 w-5 rounded-full border border-grey appearance-none',
        // Focus styles
        'focus:outline-none focus:ring-2 focus:ring-blue-focus focus:ring-offset-1',
        // Checked and disabled states
        'checked:border-midnight cursor-pointer disabled:cursor-not-allowed disabled:border-midnight-disabled',
    );

    if (noWrapper) {
        return (
            <span className="relative inline-flex items-center justify-center min-h-6 min-w-6">
                <input type="radio" id={id} className={cn(defaultClasses, className)} {...props} />
                <span
                    className={cn(
                        'absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full bg-midnight pointer-events-none',
                        'h-2.5 w-2.5',
                        'hidden peer-checked:block',
                        'peer-disabled:bg-midnight-disabled',
                    )}
                    aria-hidden="true"
                />
            </span>
        );
    }

    return (
        <label
            className={cn(
                'group flex h-full min-w-0 items-center gap-2 cursor-pointer select-none has-[:disabled]:cursor-not-allowed',
                'min-h-6',
                showBorder && ' border border-grey-dark rounded px-2 py-1',
                wrapperClassName,
            )}
        >
            <span className="relative inline-flex items-center justify-center min-h-6 min-w-6 shrink-0">
                <input
                    type="radio"
                    id={id}
                    className={cn(defaultClasses, className)}
                    aria-labelledby={label ? labelId : undefined}
                    aria-describedby={description ? descriptionId : undefined}
                    {...props}
                />
                <span
                    className={cn(
                        'absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full bg-midnight pointer-events-none',
                        'h-2.5 w-2.5',
                        'hidden peer-checked:block',
                        'peer-disabled:bg-midnight-disabled',
                    )}
                    aria-hidden="true"
                />
            </span>
            {(label || description) && (
                <span className="flex min-w-0 flex-col">
                    {label && (
                        <span id={labelId} className={cn(textVariants.label, labelClassName)}>
                            {label}
                        </span>
                    )}
                    {description && (
                        <span id={descriptionId} className={cn(textVariants.description, descriptionClassName)}>
                            {description}
                        </span>
                    )}
                </span>
            )}
        </label>
    );
};
